import {
  type OpenChangeReason,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  useTransitionStatus,
} from '@floating-ui/react';
import { interactionResponse } from '@zillow/yield-callback';
import { type SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useReducedMotion } from '../../../hooks/use-reduced-motion';
import type { ModalRootPropsInterface } from '../types/modal-root.types';
import type {
  ModalFloatingContextInterface,
  UseModalReturnInterface,
} from '../types/use-modal.types';
import { useModalRegistry } from './use-modal-registry';

export function useModal(props: Partial<ModalRootPropsInterface>): {
  modal: UseModalReturnInterface;
  floating: ModalFloatingContextInterface;
} {
  const {
    // Unique ID for modal registry
    multiModalTriggerId,

    // Open state management props
    defaultOpen,
    open: controlledOpen,
    onOpenChange: setControlledOpen,

    // Performance optimization props
    shouldAwaitInteractionResponse = true,

    // Floating UI hook props
    useClickProps,
    useDismissProps,
    useRoleProps,
  } = props;

  const { isActive, close } = useModalRegistry();

  /**
   * Accessibility IDs for `Modal.Header` and `Modal.Body`
   */
  const [labelId, setLabelId] = useState<string | undefined>();
  const [descriptionId, setDescriptionId] = useState<string | undefined>();

  /**
   * Open state management (controlled or uncontrolled)
   */
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = 'open' in props;
  const open = isControlled ? controlledOpen : uncontrolledOpen || false;

  /**
   * Floating UI setup for modal state management
   */
  const { context, refs, floatingStyles } = useFloating({
    open,
    onOpenChange: async (state, event, reason) => {
      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      updateOpenState(state, event, reason);
    },
  });

  /**
   * Floating UI interaction hooks for accessibility and behavior
   * Sets up click and dismiss event handlers, and ARIA attributes based on role
   */

  const click = useClick(context, {
    enabled: true,
    event: 'mousedown',
    ...useClickProps,
  });

  /*
   * Handle dismiss behavior with enhanced outside press detection
   * Prevents closing when clicking on:
   * - Any Modal.Content (handles multiple overlaid modals)
   * - Floating UI elements (DatePicker, Combobox, Dropdown, etc.)
   */
  const dismiss = useDismiss(context, {
    enabled: true,
    outsidePress: (event) => {
      const target = event.target as HTMLElement;

      /*
       * Prevent dismissal when clicking the backdrop's vertical scrollbar.
       *
       * Floating UI's built-in scrollbar check (offsetX > clientWidth) fails for
       * macOS overlay scrollbars because they don't occupy layout space. We detect
       * the scrollbar region ourselves using a threshold from the backdrop's right
       * edge. Only vertical overflow is possible — Modal.Content is always
       * width-constrained via maxInlineSize.
       */
      const backdrop = target.closest(
        '[data-c11n-component="Modal.Backdrop"]',
      ) as HTMLElement | null;

      if (backdrop && backdrop.scrollHeight > backdrop.clientHeight) {
        const rect = backdrop.getBoundingClientRect();

        /*
         * macOS overlay scrollbars (~12 px expanded) don't occupy layout space,
         * so the gutter width reads as 0. This constant provides a fallback
         * threshold wide enough to cover the expanded overlay track.
         */
        const OVERLAY_SCROLLBAR_WIDTH = 14;

        /*
         * Classic scrollbars (Windows, Linux, macOS "Always show scrollbars")
         * occupy layout space. offsetWidth includes the scrollbar; clientWidth
         * does not. The difference is the exact gutter width (typically 15-17 px).
         * For overlay scrollbars this difference is 0.
         */
        const gutterWidth = backdrop.offsetWidth - backdrop.clientWidth;

        /*
         * Use the larger of the two: on classic systems the gutter is already
         * wider than OVERLAY_SCROLLBAR_WIDTH, so we get a precise boundary.
         * On overlay systems the gutter is 0, so we fall back to the constant.
         */
        const threshold = Math.max(gutterWidth, OVERLAY_SCROLLBAR_WIDTH);

        if (event.clientX >= rect.right - threshold) {
          return false;
        }
      }

      /*
       * Check if click is inside any Modal.Content
       * This handles multiple adjacent modals (multiModalTriggerId pattern)
       */
      if (target.closest('[data-c11n-component="Modal.Content"]')) {
        return false;
      }

      /*
       * Check if click is inside any floating component's content.
       *
       * This prevents the modal from closing when interacting with portal-rendered
       * floating content (DatePicker calendars, Combobox dropdowns, Dropdown menus,
       * Popover content, Menu items, Tooltip content, etc.).
       *
       * These components render their content via FloatingPortal to document.body,
       * making them technically "outside" the Modal's DOM hierarchy. Without this check,
       * clicks on these elements would incorrectly trigger modal dismissal.
       *
       * We detect floating content by checking for "ContentPositioner" in the component
       * name, which is the pattern used by all Constellation components that render
       * floating content via Floating UI.
       */
      if (target.closest('[data-c11n-component*="ContentPositioner"]')) {
        return false;
      }

      /*
       * Click is outside - allow dismissal
       */
      return true;
    },
    ...useDismissProps,
  });

  const role = useRole(context, {
    role: 'dialog',
    ...useRoleProps,
  });

  const { getFloatingProps, getReferenceProps } = useInteractions([click, dismiss, role]);

  const reducedMotion = useReducedMotion();

  const { isMounted, status: transitionStatus } = useTransitionStatus(context, {
    duration: reducedMotion ? 0 : 200,
  });

  /**
   * Handles controlled or uncontrolled "open" state.
   *
   * @param newValue - New state
   * @param event - Event object
   * @param reason - Reason for state change
   */
  const updateOpenState = useCallback(
    (newValue: boolean, event?: Event, reason?: OpenChangeReason) => {
      if (isControlled) {
        setControlledOpen?.(newValue, event, reason);
      } else {
        setUncontrolledOpen(newValue);
        setControlledOpen?.(newValue, event, reason);
      }

      // Close multi-trigger modal
      if (multiModalTriggerId && newValue === false) {
        close(multiModalTriggerId);
      }
    },
    [isControlled, setControlledOpen, close, multiModalTriggerId],
  );

  /**
   * Handles `Modal.Close` and `Modal.CloseButton` click actions.
   *
   * @param event - Event object
   */
  const onClickClose = useCallback(
    async (event: SyntheticEvent) => {
      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      // Update modal state
      updateOpenState(false, event as unknown as Event, 'click');
    },
    [updateOpenState, shouldAwaitInteractionResponse],
  );

  /**
   * Open modal if `multiModalTriggerId` exists and is active in registry
   */
  useEffect(() => {
    if (multiModalTriggerId && isActive(multiModalTriggerId)) {
      updateOpenState(true);
    }
  }, [multiModalTriggerId, isActive, updateOpenState]);

  /**
   * Returns all necessary state and handlers for the modal
   */
  const modal = useMemo(
    () => ({
      // Open state props and handlers
      open,
      isMounted,
      transitionStatus,
      onClickClose,

      // Trigger and reference refs
      refs,

      // Accessibility props and handlers
      multiModalTriggerId,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,

      // Trigger and reference props
      getReferenceProps,

      // Styles
      floatingStyles,
    }),
    [
      open,
      isMounted,
      transitionStatus,
      onClickClose,
      refs,
      multiModalTriggerId,
      labelId,
      descriptionId,
      getReferenceProps,
      floatingStyles,
    ],
  );

  /*
   * Floating-ui-bridged data. Keep it on a separate context so only
   * Modal.Content pays for fresh Floating UI values.
   */
  const floating = { context, getFloatingProps };

  return { modal, floating };
}
